import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Tracks open dialogs / popups in open-order so keyboard dismissal (ESC) can be
 * routed to the topmost one only. When dialogs are stacked — a `ui-side-dialog`
 * with another slid in on top, a `PopupService` popup opened from another popup
 * (e.g. payables-dialog → counterpart-dialog), or a confirm overlay on top of a
 * side panel — a single ESC must close just the active/topmost layer rather than
 * every open instance.
 *
 * Entries are typed as `object` (compared by reference) so this service stays
 * free of any dependency on the participating components and can be shared across
 * every dialog/overlay type without circular imports.
 */
@Injectable({ providedIn: 'root' })
export class DialogStackService {
    private stack: object[] = [];
    private readonly stackChangeSubject = new Subject<void>();

    /** Emits whenever a dialog is registered or unregistered. */
    readonly changes$ = this.stackChangeSubject.asObservable();

    register(dialog: object): void {
        if (!this.stack.includes(dialog)) {
            this.stack.push(dialog);
            this.stackChangeSubject.next();
        }
    }

    unregister(dialog: object): void {
        const i = this.stack.indexOf(dialog);
        if (i > -1) {
            this.stack.splice(i, 1);
            this.stackChangeSubject.next();
        }
    }

    isTopmost(dialog: object): boolean {
        return this.stack.length > 0 && this.stack[this.stack.length - 1] === dialog;
    }

    getLevel(dialog: object): number {
        return this.stack.indexOf(dialog);
    }
}
